import { useState, useEffect } from 'react';
import AdminCRUDLayout from '../../components/admin/AdminCRUDLayout';
import UnifiedDataTable from '../../components/admin/UnifiedDataTable';
import StandardModal from '../../components/admin/StandardModal';
import StandardForm from '../../components/admin/StandardForm';
import categoryService from '../../services/categoryService';
import { formatCategoryInfo, validateBirthYearRange, getValidYearRange } from '../../utils/categoryUtils';

/**
 * Versión simplificada para probar la funcionalidad básica
 */
const CategoryManagementSimple = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados para modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Estado para el elemento actual
  const [currentItem, setCurrentItem] = useState(null);

  // Estado para formularios
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  // Cargar categorías
  const loadCategories = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await categoryService.getAll();
      console.log('Respuesta del servicio:', response);

      // Procesar datos usando utilidades
      const data = response.data || response.categories || response;
      const processedData = Array.isArray(data) ? data.map(category => {
        const formatted = formatCategoryInfo(category);
        return {
          ...category,
          ...formatted,
          // Mantener compatibilidad con campos existentes
          ageRangeText: formatted.rangeText || `${category.ageRange?.min || 0}-${category.ageRange?.max || 0} años`
        };
      }) : [];

      setCategories(processedData);
    } catch (err) {
      console.error('Error cargando categorías:', err);
      setError(err.message || 'Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos al montar
  useEffect(() => {
    loadCategories();
  }, []);

  // Funciones para manejar modales
  const openCreateModal = () => {
    setFormData({});
    setFormErrors({});
    setCurrentItem(null);
    setShowCreateModal(true);
  };

  const openEditModal = (item) => {
    setCurrentItem(item);
    setFormData({ ...item });
    setFormErrors({});
    setShowEditModal(true);
  };

  const openViewModal = (item) => {
    setCurrentItem(item);
    setShowViewModal(true);
  };

  const openDeleteModal = (item) => {
    setCurrentItem(item);
    setShowDeleteModal(true);
  };

  const closeAllModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setShowDeleteModal(false);
    setCurrentItem(null);
    setFormData({});
    setFormErrors({});
  };

  // Función para validar datos
  const validateData = (data) => {
    const errors = {};

    if (!data.name?.trim()) {
      errors.name = 'El nombre es obligatorio';
    }

    // Validación de año de nacimiento (NUEVO - prioritario)
    if (data.birthYearRange) {
      const { start, end } = data.birthYearRange;
      const validRange = getValidYearRange();

      if (!start || start < validRange.min || start > validRange.max) {
        errors['birthYearRange.start'] = `El año inicial debe estar entre ${validRange.min} y ${validRange.max}`;
      }

      if (!end || end < validRange.min || end > validRange.max) {
        errors['birthYearRange.end'] = `El año final debe estar entre ${validRange.min} y ${validRange.max}`;
      }

      if (start && end && start > end) {
        errors['birthYearRange.end'] = 'El año final no puede ser menor que el inicial';
      }

      if (start && end && !validateBirthYearRange(start, end)) {
        errors['birthYearRange.start'] = 'Rango de años de nacimiento inválido';
      }
    }

    // Validación de edad (mantener para compatibilidad)
    if (data.ageRange && !data.birthYearRange) {
      if (!data.ageRange?.min || data.ageRange.min < 3) {
        errors['ageRange.min'] = 'La edad mínima debe ser al menos 3 años';
      }

      if (!data.ageRange?.max || data.ageRange.max > 18) {
        errors['ageRange.max'] = 'La edad máxima no puede ser mayor a 18 años';
      }

      if (data.ageRange?.min && data.ageRange?.max && data.ageRange.min >= data.ageRange.max) {
        errors['ageRange.max'] = 'La edad máxima debe ser mayor que la mínima';
      }
    }

    if (!data.playersPerTeam || data.playersPerTeam < 1) {
      errors.playersPerTeam = 'Debe haber al menos 1 jugador por equipo';
    }

    if (!data.matchDuration || data.matchDuration < 10) {
      errors.matchDuration = 'La duración mínima es 10 minutos';
    }

    return errors;
  };

  // Función para crear categoría
  const handleCreate = async (e) => {
    e.preventDefault();
    console.log('handleCreate ejecutado con datos:', formData);
    setFormLoading(true);
    setFormErrors({});

    const errors = validateData(formData);
    if (Object.keys(errors).length > 0) {
      console.log('Errores de validación:', errors);
      setFormErrors(errors);
      setFormLoading(false);
      return;
    }

    try {
      console.log('Enviando datos al servicio:', formData);
      await categoryService.create(formData);
      closeAllModals();
      loadCategories();
      console.log('Categoría creada correctamente');
    } catch (err) {
      console.error('Error creando categoría:', err);
      setError(err.message || 'Error al crear la categoría');
    } finally {
      setFormLoading(false);
    }
  };

  // Función para actualizar categoría
  const handleUpdate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormErrors({});

    const errors = validateData(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setFormLoading(false);
      return;
    }

    try {
      const id = currentItem?.id || currentItem?._id;
      await categoryService.update(id, formData);
      closeAllModals();
      loadCategories();
      console.log('Categoría actualizada correctamente');
    } catch (err) {
      console.error('Error actualizando categoría:', err);
      setError(err.message || 'Error al actualizar la categoría');
    } finally {
      setFormLoading(false);
    }
  };

  // Función para eliminar categoría
  const handleDelete = async () => {
    setFormLoading(true);

    try {
      const id = currentItem?.id || currentItem?._id;
      await categoryService.delete(id);
      closeAllModals();
      loadCategories();
      console.log('Categoría eliminada correctamente');
    } catch (err) {
      console.error('Error eliminando categoría:', err);
      setError(err.message || 'Error al eliminar la categoría');
    } finally {
      setFormLoading(false);
    }
  };

  // Función para activar/desactivar categoría
  const handleToggleActive = async (item) => {
    try {
      const id = item?.id || item?._id;
      await categoryService.update(id, { ...item, active: !item.active });
      loadCategories();
      console.log(`Categoría ${item.active ? 'desactivada' : 'activada'} correctamente`);
    } catch (err) {
      console.error('Error cambiando estado de categoría:', err);
      setError(err.message || 'Error al cambiar el estado de la categoría');
    }
  };

  // Función para manejar cambios en formulario
  const handleFormChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Limpiar error del campo si existe
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Configuración de columnas
  const columns = [
    {
      key: 'name',
      label: 'Nombre',
      render: (row) => (
        <div className="category-name">
          <span
            className="category-color"
            style={{
              backgroundColor: row.color || '#1976d2',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              display: 'inline-block',
              marginRight: '8px'
            }}
          ></span>
          <span>{row.name}</span>
        </div>
      )
    },
    {
      key: 'rangeText',
      label: 'Rango',
      render: (row) => (
        <div className="range-info">
          <div className="primary-range">
            {row.rangeText || row.ageRangeText}
          </div>
          {row.birthYearRange && (
            <div className="secondary-range" style={{ fontSize: '0.8em', color: '#666' }}>
              Años: {row.birthYearRange.start}-{row.birthYearRange.end}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'gender',
      label: 'Género',
      render: (row) => (
        <span className={`gender-badge gender-${row.gender}`}>
          {row.gender === 'boys' && 'Niños'}
          {row.gender === 'girls' && 'Niñas'}
          {row.gender === 'mixed' && 'Mixto'}
        </span>
      )
    },
    {
      key: 'playersPerTeam',
      label: 'Jugadores/Equipo'
    },
    {
      key: 'fieldSize',
      label: 'Tamaño Campo',
      render: (row) => (
        <span className={`field-size-badge field-${row.fieldSize}`}>
          {row.fieldSize === 'small' && 'Reducido'}
          {row.fieldSize === 'medium' && 'Medio'}
          {row.fieldSize === 'full' && 'Completo'}
        </span>
      )
    },
    {
      key: 'matchDuration',
      label: 'Duración (min)'
    },
    {
      key: 'active',
      label: 'Estado',
      render: (row) => (
        <span className={`status-badge ${row.active ? 'active' : 'inactive'}`}>
          {row.active ? 'Activa' : 'Inactiva'}
        </span>
      )
    }
  ];

  // Acciones completas
  const actions = [
    {
      key: 'view',
      icon: 'visibility',
      label: 'Ver detalles',
      type: 'default',
      onClick: openViewModal
    },
    {
      key: 'edit',
      icon: 'edit',
      label: 'Editar',
      type: 'primary',
      onClick: openEditModal
    },
    {
      key: 'toggle',
      icon: (row) => row.active ? 'visibility_off' : 'visibility',
      label: (row) => row.active ? 'Desactivar' : 'Activar',
      type: (row) => row.active ? 'warning' : 'success',
      onClick: handleToggleActive
    },
    {
      key: 'delete',
      icon: 'delete',
      label: 'Eliminar',
      type: 'danger',
      onClick: openDeleteModal
    }
  ];

  const clearError = () => setError(null);

  // Configuración de campos para el formulario
  const validYearRange = getValidYearRange();

  const formFields = [
    {
      key: 'name',
      label: 'Nombre',
      type: 'text',
      required: true,
      placeholder: 'Ej: Prebenjamín'
    },
    {
      key: 'description',
      label: 'Descripción',
      type: 'textarea',
      placeholder: 'Descripción de la categoría',
      rows: 3
    },
    // NUEVOS campos de año de nacimiento (prioritarios)
    {
      key: 'birthYearRange.start',
      label: 'Año de Nacimiento Inicial',
      type: 'number',
      required: true,
      min: validYearRange.min,
      max: validYearRange.max,
      placeholder: `Ej: ${validYearRange.min + 2}`,
      help: `Año de nacimiento de los jugadores más viejos (${validYearRange.min}-${validYearRange.max})`
    },
    {
      key: 'birthYearRange.end',
      label: 'Año de Nacimiento Final',
      type: 'number',
      required: true,
      min: validYearRange.min,
      max: validYearRange.max,
      placeholder: `Ej: ${validYearRange.max - 2}`,
      help: `Año de nacimiento de los jugadores más jóvenes (${validYearRange.min}-${validYearRange.max})`
    },
    {
      key: 'gender',
      label: 'Género',
      type: 'select',
      required: true,
      options: [
        { value: 'boys', label: 'Niños' },
        { value: 'girls', label: 'Niñas' },
        { value: 'mixed', label: 'Mixto' }
      ]
    },
    {
      key: 'playersPerTeam',
      label: 'Jugadores por equipo',
      type: 'number',
      required: true,
      min: 1,
      max: 11
    },
    {
      key: 'fieldSize',
      label: 'Tamaño de campo',
      type: 'select',
      required: true,
      options: [
        { value: 'small', label: 'Reducido' },
        { value: 'medium', label: 'Medio' },
        { value: 'full', label: 'Completo' }
      ]
    },
    {
      key: 'matchDuration',
      label: 'Duración del partido (minutos)',
      type: 'number',
      required: true,
      min: 10,
      max: 90
    },
    {
      key: 'color',
      label: 'Color identificativo',
      type: 'text',
      placeholder: '#1976d2'
    },
    {
      key: 'icon',
      label: 'Icono (Material Icon)',
      type: 'text',
      placeholder: 'sports_soccer'
    },
    {
      key: 'active',
      label: 'Categoría activa',
      type: 'checkbox'
    }
  ];

  // Botón de acción principal
  const headerActions = (
    <button className="btn btn-primary" onClick={openCreateModal}>
      <span className="material-icons">add</span>
      Añadir Categoría
    </button>
  );

  return (
    <AdminCRUDLayout
      title="Gestión de Categorías"
      subtitle="Administra las categorías de la plataforma"
      headerActions={headerActions}
      loading={loading}
      error={error}
      onErrorDismiss={clearError}
    >
      <UnifiedDataTable
        columns={columns}
        data={categories}
        actions={actions}
        searchable={true}
        pagination={true}
        onRowClick={openViewModal}
        emptyMessage="No hay categorías disponibles"
      />

      {/* Modal de creación */}
      <StandardModal
        isOpen={showCreateModal}
        onClose={closeAllModals}
        title="Crear Categoría"
        type="create"
        size="large"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeAllModals}
              disabled={formLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="category-form"
              className="btn btn-primary"
              disabled={formLoading}
            >
              {formLoading ? 'Creando...' : 'Crear Categoría'}
            </button>
          </>
        }
      >
        <StandardForm
          id="category-form"
          fields={formFields}
          data={formData}
          onChange={handleFormChange}
          onSubmit={handleCreate}
          errors={formErrors}
          loading={formLoading}
          layout="horizontal"
          submitLabel=""
          cancelLabel=""
          onCancel={null}
        />
      </StandardModal>

      {/* Modal de edición */}
      <StandardModal
        isOpen={showEditModal}
        onClose={closeAllModals}
        title="Editar Categoría"
        type="edit"
        size="large"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeAllModals}
              disabled={formLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="category-edit-form"
              className="btn btn-primary"
              disabled={formLoading}
            >
              {formLoading ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </>
        }
      >
        <StandardForm
          id="category-edit-form"
          fields={formFields}
          data={formData}
          onChange={handleFormChange}
          onSubmit={handleUpdate}
          errors={formErrors}
          loading={formLoading}
          layout="horizontal"
          submitLabel=""
          cancelLabel=""
          onCancel={null}
        />
      </StandardModal>

      {/* Modal de visualización */}
      <StandardModal
        isOpen={showViewModal}
        onClose={closeAllModals}
        title="Detalles de la Categoría"
        type="view"
        size="large"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeAllModals}
            >
              Cerrar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                closeAllModals();
                setTimeout(() => openEditModal(currentItem), 100);
              }}
            >
              Editar Categoría
            </button>
          </>
        }
      >
        {currentItem && (
          <div className="category-details">
            <div className="detail-section">
              <h4>Información General</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Nombre:</label>
                  <div className="category-name-display">
                    <span
                      className="category-color"
                      style={{
                        backgroundColor: currentItem.color || '#1976d2',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        display: 'inline-block',
                        marginRight: '8px'
                      }}
                    ></span>
                    <span>{currentItem.name}</span>
                  </div>
                </div>
                <div className="detail-item">
                  <label>Descripción:</label>
                  <span>{currentItem.description || 'Sin descripción'}</span>
                </div>
                <div className="detail-item">
                  <label>Año de nacimiento:</label>
                  <span>
                    {currentItem.birthYearRange
                      ? `${currentItem.birthYearRange.start} - ${currentItem.birthYearRange.end}`
                      : 'No especificado'
                    }
                  </span>
                </div>
                <div className="detail-item">
                  <label>Género:</label>
                  <span className={`gender-badge gender-${currentItem.gender}`}>
                    {currentItem.gender === 'boys' && 'Niños'}
                    {currentItem.gender === 'girls' && 'Niñas'}
                    {currentItem.gender === 'mixed' && 'Mixto'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Estado:</label>
                  <span className={`status-badge ${currentItem.active ? 'active' : 'inactive'}`}>
                    {currentItem.active ? 'Activa' : 'Inactiva'}
                  </span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>Configuración de Juego</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Jugadores por equipo:</label>
                  <span>{currentItem.playersPerTeam}</span>
                </div>
                <div className="detail-item">
                  <label>Tamaño de campo:</label>
                  <span className={`field-size-badge field-${currentItem.fieldSize}`}>
                    {currentItem.fieldSize === 'small' && 'Reducido'}
                    {currentItem.fieldSize === 'medium' && 'Medio'}
                    {currentItem.fieldSize === 'full' && 'Completo'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Duración del partido:</label>
                  <span>{currentItem.matchDuration} minutos</span>
                </div>
                <div className="detail-item">
                  <label>Icono:</label>
                  <span>
                    <span className="material-icons" style={{ verticalAlign: 'middle', marginRight: '4px' }}>
                      {currentItem.icon || 'sports_soccer'}
                    </span>
                    {currentItem.icon || 'sports_soccer'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </StandardModal>

      {/* Modal de eliminación */}
      <StandardModal
        isOpen={showDeleteModal}
        onClose={closeAllModals}
        title="Confirmar Eliminación"
        type="delete"
        size="small"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeAllModals}
              disabled={formLoading}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={formLoading}
            >
              {formLoading ? 'Eliminando...' : 'Eliminar'}
            </button>
          </>
        }
      >
        {currentItem && (
          <div className="delete-confirmation">
            <div className="warning-icon">
              <span className="material-icons">warning</span>
            </div>
            <p>
              ¿Estás seguro de que deseas eliminar la categoría{' '}
              <strong>{currentItem.name}</strong>?
            </p>
            <p className="warning-text">
              Esta acción no se puede deshacer.
            </p>
          </div>
        )}
      </StandardModal>
    </AdminCRUDLayout>
  );
};

export default CategoryManagementSimple;
